import { useState, type ReactNode } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useContentToken,
  EditableToken,
  RichTextContent,
  useEditMode,
} from '@vibesites/runtime/react';
import { cn } from '@vibesites/ui-primitives';

/**
 * FeeTransparency — fee disclosure accordion matching Chesterfield Flats'
 * floor-plans pattern: three expandable panels (Required / Optional /
 * Situational) with Fee Name · Cost · Frequency · Fee Type tables.
 *
 * Fee-TYPE labels and frequencies are the disclosure template; only the
 * per-property AMOUNTS are editable content tokens.
 */

const NA = '—';
function isNA(value: string | null | undefined): boolean {
  if (value == null) return true;
  const v = value.trim();
  return v === '' || v === NA;
}

/** True when the cost should show a leading $ (numeric amounts without one). */
function shouldPrefixDollar(value: string | null | undefined): boolean {
  if (isNA(value)) return false;
  const v = value!.trim();
  if (v.startsWith('$')) return false;
  return /^[\d.,]/.test(v);
}

function costClass(value: string | null | undefined): string {
  return cn('ft-cost', shouldPrefixDollar(value) && 'ft-cost--dollar');
}

type FeeType = 'Required' | 'Optional' | 'Situational';
type BadgeVariant = 'required' | 'optional' | 'situational';

type FeeRow = {
  name: string;
  value: string | null | undefined;
  frequency: string;
  feeType: FeeType;
  badge: BadgeVariant;
  node: ReactNode;
};

function FeeBadge({ variant, children }: { variant: BadgeVariant; children: ReactNode }) {
  return (
    <span className={cn('ft-badge', `ft-badge--${variant}`)}>{children}</span>
  );
}

function FeeTable({
  rows,
  showAll,
  footer,
}: {
  rows: FeeRow[];
  showAll: boolean;
  footer?: ReactNode;
}) {
  const visible = showAll ? rows : rows.filter((r) => !isNA(r.value));

  if (visible.length === 0 && !footer) {
    return (
      <p className="ft-empty">No fees in this category apply at this community.</p>
    );
  }

  return (
    <div className="ft-table-wrap">
      {visible.length > 0 ? (
        <table className="ft-table">
          <thead>
            <tr>
              <th scope="col">Fee Name</th>
              <th scope="col">Cost</th>
              <th scope="col">Frequency</th>
              <th scope="col">Fee Type</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.name}>
                <td data-label="Fee Name">{row.name}</td>
                <td data-label="Cost">{row.node}</td>
                <td data-label="Frequency">{row.frequency}</td>
                <td data-label="Fee Type">
                  <FeeBadge variant={row.badge}>{row.feeType}</FeeBadge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="ft-empty">No fees in this category apply at this community.</p>
      )}
      {footer}
    </div>
  );
}

function FeePanel({
  title,
  badgeLabel,
  badgeVariant,
  description,
  rows,
  open,
  onToggle,
  showAll,
  footer,
}: {
  title: string;
  badgeLabel: string;
  badgeVariant: BadgeVariant;
  description: ReactNode;
  rows: FeeRow[];
  open: boolean;
  onToggle: () => void;
  showAll: boolean;
  footer?: ReactNode;
}) {
  return (
    <div className={cn('ft-panel', open && 'ft-panel--open')}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="ft-panel-trigger"
      >
        <span className="ft-panel-title">
          {title}
          <FeeBadge variant={badgeVariant}>{badgeLabel}</FeeBadge>
        </span>
        <span className="ft-panel-icon" aria-hidden="true" />
      </button>

      <div className="ft-panel-body" data-open={open} aria-hidden={!open}>
        <div className="ft-panel-body-inner">
          {description}
          <FeeTable rows={rows} showAll={showAll} footer={footer} />
        </div>
      </div>
    </div>
  );
}

interface FeeTransparencyProps {
  /** When false, hides the section heading and intro (Floor Plans). Default true. */
  showHeader?: boolean;
  /** When true, stretches the content to the full page width (Floor Plans). */
  wide?: boolean;
}

export function FeeTransparency({ showHeader = true, wide = false }: FeeTransparencyProps) {
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;

  const heading = useContentToken('feetransparency.heading');

  const applicationFee = useContentToken('feetransparency.application_fee');
  const administrativeFee = useContentToken('feetransparency.administrative_fee');
  const securityDeposit = useContentToken('feetransparency.security_deposit');
  const reservationFee = useContentToken('feetransparency.reservation_fee');
  const monthToMonthFee = useContentToken('feetransparency.month_to_month_fee');
  const riskFee = useContentToken('feetransparency.risk_fee');
  const moveOutCleaningFee = useContentToken('feetransparency.move_out_cleaning_fee');
  const earlyTerminationFee = useContentToken('feetransparency.early_termination_fee');
  const intraCommunityTransferFee = useContentToken('feetransparency.intra_community_transfer_fee');
  const lateFee = useContentToken('feetransparency.late_fee');
  const creditBuilder = useContentToken('feetransparency.credit_builder');
  const accessDeviceCost = useContentToken('feetransparency.access_device_cost');
  const shortTermRentalFee = useContentToken('feetransparency.short_term_rental_fee');
  const occupancyFee = useContentToken('feetransparency.occupancy_fee');

  const electricityConnectionFee = useContentToken('feetransparency.electricity_connection_fee');
  const electricityDisconnectionFee = useContentToken('feetransparency.electricity_disconnection_fee');
  const utilityElectric = useContentToken('feetransparency.utility_electric');
  const utilityGas = useContentToken('feetransparency.utility_gas');
  const utilityWaterSewer = useContentToken('feetransparency.utility_water_sewer');
  const utilityReimbFee = useContentToken('feetransparency.utility_reimb_fee');
  const utilitySetupFee = useContentToken('feetransparency.utility_setup_fee');
  const utilityDisconnectFee = useContentToken('feetransparency.utility_disconnect_fee');
  const utilityAdminBillingFee = useContentToken('feetransparency.utility_admin_billing_fee');

  const parkingCarport = useContentToken('feetransparency.parking_carport');
  const parkingGarage = useContentToken('feetransparency.parking_garage');
  const parkingMonthly = useContentToken('feetransparency.parking_monthly');
  const parkingPass = useContentToken('feetransparency.parking_pass');
  const storageUnitFee = useContentToken('feetransparency.storage_unit_fee');

  const petRent = useContentToken('feetransparency.pet_rent');
  const petFee = useContentToken('feetransparency.pet_fee');
  const petFeeSecondPet = useContentToken('feetransparency.pet_fee_second_pet');
  const petRentSecondPet = useContentToken('feetransparency.pet_rent_second_pet');

  const communityAmenityFee = useContentToken('feetransparency.community_amenity_fee');
  const valetTrashFee = useContentToken('feetransparency.valet_trash_fee');
  const pestControlFee = useContentToken('feetransparency.pest_control_fee');
  const residentTechFee = useContentToken('feetransparency.resident_tech_fee');
  const trashNonValet = useContentToken('feetransparency.trash_non_valet');
  const trashReimbFee = useContentToken('feetransparency.trash_reimb_fee');
  const trashAdminFee = useContentToken('feetransparency.trash_admin_fee');
  const trashHaulingFee = useContentToken('feetransparency.trash_hauling_fee');
  const buildingLiabilityCaptive = useContentToken('feetransparency.building_liability_captive');
  const poolFee = useContentToken('feetransparency.pool_fee');
  const packageLockers = useContentToken('feetransparency.package_lockers');
  const washerDryerFee = useContentToken('feetransparency.washer_dryer_fee');
  const residentServicesTotal = useContentToken('feetransparency.resident_services_total');

  const showAll = isEditMode;

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    required: true,
  });
  const toggle = (id: string) =>
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));

  const requiredRows: FeeRow[] = [
    { name: 'Application Fee', value: applicationFee, frequency: 'One-Time', feeType: 'Required', badge: 'required', node: <span className={costClass(applicationFee)}><EditableToken path="feetransparency.application_fee" as="span">{applicationFee}</EditableToken></span> },
    { name: 'Administrative Fee', value: administrativeFee, frequency: 'One-Time', feeType: 'Required', badge: 'required', node: <span className={costClass(administrativeFee)}><EditableToken path="feetransparency.administrative_fee" as="span">{administrativeFee}</EditableToken></span> },
    { name: 'Security Deposit', value: securityDeposit, frequency: 'One-Time at Move-In', feeType: 'Required', badge: 'required', node: <span className={costClass(securityDeposit)}><EditableToken path="feetransparency.security_deposit" as="span">{securityDeposit}</EditableToken></span> },
    { name: 'Reservation Fee', value: reservationFee, frequency: 'One-Time', feeType: 'Required', badge: 'required', node: <span className={costClass(reservationFee)}><EditableToken path="feetransparency.reservation_fee" as="span">{reservationFee}</EditableToken></span> },
    { name: 'Community Amenity Fee', value: communityAmenityFee, frequency: 'One-Time', feeType: 'Required', badge: 'required', node: <span className={costClass(communityAmenityFee)}><EditableToken path="feetransparency.community_amenity_fee" as="span">{communityAmenityFee}</EditableToken></span> },
    { name: 'Valet Trash Fee', value: valetTrashFee, frequency: 'Monthly', feeType: 'Required', badge: 'required', node: <span className={costClass(valetTrashFee)}><EditableToken path="feetransparency.valet_trash_fee" as="span">{valetTrashFee}</EditableToken></span> },
    { name: 'Utility Set-up Fee', value: utilitySetupFee, frequency: 'One-Time', feeType: 'Required', badge: 'required', node: <span className={costClass(utilitySetupFee)}><EditableToken path="feetransparency.utility_setup_fee" as="span">{utilitySetupFee}</EditableToken></span> },
    { name: 'Utility — Gas', value: utilityGas, frequency: 'Monthly', feeType: 'Required', badge: 'required', node: <span className={costClass(utilityGas)}><EditableToken path="feetransparency.utility_gas" as="span">{utilityGas}</EditableToken></span> },
    { name: 'Utility — Water/Sewer', value: utilityWaterSewer, frequency: 'Monthly', feeType: 'Required', badge: 'required', node: <span className={costClass(utilityWaterSewer)}><EditableToken path="feetransparency.utility_water_sewer" as="span">{utilityWaterSewer}</EditableToken></span> },
    { name: 'Utility — Electric', value: utilityElectric, frequency: 'Monthly', feeType: 'Required', badge: 'required', node: <span className={costClass(utilityElectric)}><EditableToken path="feetransparency.utility_electric" as="span">{utilityElectric}</EditableToken></span> },
    { name: 'Electricity Connection Fee', value: electricityConnectionFee, frequency: 'One-Time', feeType: 'Required', badge: 'required', node: <span className={costClass(electricityConnectionFee)}><EditableToken path="feetransparency.electricity_connection_fee" as="span">{electricityConnectionFee}</EditableToken></span> },
    { name: 'Electricity Disconnection Fee', value: electricityDisconnectionFee, frequency: 'One-Time', feeType: 'Required', badge: 'required', node: <span className={costClass(electricityDisconnectionFee)}><EditableToken path="feetransparency.electricity_disconnection_fee" as="span">{electricityDisconnectionFee}</EditableToken></span> },
    { name: 'Utility Reimbursement Fee', value: utilityReimbFee, frequency: 'Monthly', feeType: 'Required', badge: 'required', node: <span className={costClass(utilityReimbFee)}><EditableToken path="feetransparency.utility_reimb_fee" as="span">{utilityReimbFee}</EditableToken></span> },
    { name: 'Utility Disconnect Fee', value: utilityDisconnectFee, frequency: 'One-Time', feeType: 'Required', badge: 'required', node: <span className={costClass(utilityDisconnectFee)}><EditableToken path="feetransparency.utility_disconnect_fee" as="span">{utilityDisconnectFee}</EditableToken></span> },
    { name: 'Utility Administrative Billing Fee', value: utilityAdminBillingFee, frequency: 'Monthly', feeType: 'Required', badge: 'required', node: <span className={costClass(utilityAdminBillingFee)}><EditableToken path="feetransparency.utility_admin_billing_fee" as="span">{utilityAdminBillingFee}</EditableToken></span> },
    { name: 'Pest Control Fee', value: pestControlFee, frequency: 'Monthly', feeType: 'Required', badge: 'required', node: <span className={costClass(pestControlFee)}><EditableToken path="feetransparency.pest_control_fee" as="span">{pestControlFee}</EditableToken></span> },
    { name: 'Resident Tech Fee', value: residentTechFee, frequency: 'Monthly', feeType: 'Required', badge: 'required', node: <span className={costClass(residentTechFee)}><EditableToken path="feetransparency.resident_tech_fee" as="span">{residentTechFee}</EditableToken></span> },
    { name: 'Trash Fee — Non-Valet', value: trashNonValet, frequency: 'Monthly', feeType: 'Required', badge: 'required', node: <span className={costClass(trashNonValet)}><EditableToken path="feetransparency.trash_non_valet" as="span">{trashNonValet}</EditableToken></span> },
    { name: 'Trash Reimbursement Fee', value: trashReimbFee, frequency: 'Monthly', feeType: 'Required', badge: 'required', node: <span className={costClass(trashReimbFee)}><EditableToken path="feetransparency.trash_reimb_fee" as="span">{trashReimbFee}</EditableToken></span> },
    { name: 'Trash Administrative Fee', value: trashAdminFee, frequency: 'Monthly', feeType: 'Required', badge: 'required', node: <span className={costClass(trashAdminFee)}><EditableToken path="feetransparency.trash_admin_fee" as="span">{trashAdminFee}</EditableToken></span> },
    { name: 'Trash Hauling Fee', value: trashHaulingFee, frequency: 'Monthly', feeType: 'Required', badge: 'required', node: <span className={costClass(trashHaulingFee)}><EditableToken path="feetransparency.trash_hauling_fee" as="span">{trashHaulingFee}</EditableToken></span> },
    { name: 'Building Liability Captive', value: buildingLiabilityCaptive, frequency: 'Monthly', feeType: 'Required', badge: 'required', node: <span className={costClass(buildingLiabilityCaptive)}><EditableToken path="feetransparency.building_liability_captive" as="span">{buildingLiabilityCaptive}</EditableToken></span> },
    { name: 'Pool Fee', value: poolFee, frequency: 'Monthly', feeType: 'Required', badge: 'required', node: <span className={costClass(poolFee)}><EditableToken path="feetransparency.pool_fee" as="span">{poolFee}</EditableToken></span> },
    { name: 'Package Lockers', value: packageLockers, frequency: 'Monthly', feeType: 'Required', badge: 'required', node: <span className={costClass(packageLockers)}><EditableToken path="feetransparency.package_lockers" as="span">{packageLockers}</EditableToken></span> },
    { name: 'Access Device Cost', value: accessDeviceCost, frequency: 'One-Time', feeType: 'Required', badge: 'required', node: <span className={costClass(accessDeviceCost)}><EditableToken path="feetransparency.access_device_cost" as="span">{accessDeviceCost}</EditableToken></span> },
    { name: 'Occupancy Fee', value: occupancyFee, frequency: 'Monthly', feeType: 'Required', badge: 'required', node: <span className={costClass(occupancyFee)}><EditableToken path="feetransparency.occupancy_fee" as="span">{occupancyFee}</EditableToken></span> },
  ];

  const optionalRows: FeeRow[] = [
    { name: 'Pet Fee', value: petFee, frequency: 'One-Time', feeType: 'Optional', badge: 'optional', node: <span className={costClass(petFee)}><EditableToken path="feetransparency.pet_fee" as="span">{petFee}</EditableToken></span> },
    { name: 'Pet Rent', value: petRent, frequency: 'Monthly', feeType: 'Optional', badge: 'optional', node: <span className={costClass(petRent)}><EditableToken path="feetransparency.pet_rent" as="span">{petRent}</EditableToken></span> },
    { name: 'Pet Fee (Second Pet)', value: petFeeSecondPet, frequency: 'One-Time', feeType: 'Optional', badge: 'optional', node: <span className={costClass(petFeeSecondPet)}><EditableToken path="feetransparency.pet_fee_second_pet" as="span">{petFeeSecondPet}</EditableToken></span> },
    { name: 'Pet Rent (Second Pet)', value: petRentSecondPet, frequency: 'Monthly', feeType: 'Optional', badge: 'optional', node: <span className={costClass(petRentSecondPet)}><EditableToken path="feetransparency.pet_rent_second_pet" as="span">{petRentSecondPet}</EditableToken></span> },
    { name: 'Washer/Dryer Fee', value: washerDryerFee, frequency: 'Monthly', feeType: 'Optional', badge: 'optional', node: <span className={costClass(washerDryerFee)}><EditableToken path="feetransparency.washer_dryer_fee" as="span">{washerDryerFee}</EditableToken></span> },
    { name: 'Parking Fee — Carport', value: parkingCarport, frequency: 'Monthly', feeType: 'Optional', badge: 'optional', node: <span className={costClass(parkingCarport)}><EditableToken path="feetransparency.parking_carport" as="span">{parkingCarport}</EditableToken></span> },
    { name: 'Parking Fee — Garage', value: parkingGarage, frequency: 'Monthly', feeType: 'Optional', badge: 'optional', node: <span className={costClass(parkingGarage)}><EditableToken path="feetransparency.parking_garage" as="span">{parkingGarage}</EditableToken></span> },
    { name: 'Parking Fee — Monthly', value: parkingMonthly, frequency: 'Monthly', feeType: 'Optional', badge: 'optional', node: <span className={costClass(parkingMonthly)}><EditableToken path="feetransparency.parking_monthly" as="span">{parkingMonthly}</EditableToken></span> },
    { name: 'Parking Pass', value: parkingPass, frequency: 'One-Time', feeType: 'Optional', badge: 'optional', node: <span className={costClass(parkingPass)}><EditableToken path="feetransparency.parking_pass" as="span">{parkingPass}</EditableToken></span> },
    { name: 'Storage Unit Fee', value: storageUnitFee, frequency: 'Monthly', feeType: 'Optional', badge: 'optional', node: <span className={costClass(storageUnitFee)}><EditableToken path="feetransparency.storage_unit_fee" as="span">{storageUnitFee}</EditableToken></span> },
    { name: 'Credit Builder', value: creditBuilder, frequency: 'Monthly', feeType: 'Optional', badge: 'optional', node: <span className={costClass(creditBuilder)}><EditableToken path="feetransparency.credit_builder" as="span">{creditBuilder}</EditableToken></span> },
    { name: 'Short-Term Rental Fee', value: shortTermRentalFee, frequency: 'Monthly', feeType: 'Optional', badge: 'optional', node: <span className={costClass(shortTermRentalFee)}><EditableToken path="feetransparency.short_term_rental_fee" as="span">{shortTermRentalFee}</EditableToken></span> },
  ];

  const situationalRows: FeeRow[] = [
    { name: 'Month-to-Month Fee', value: monthToMonthFee, frequency: 'Monthly', feeType: 'Situational', badge: 'situational', node: <span className={costClass(monthToMonthFee)}><EditableToken path="feetransparency.month_to_month_fee" as="span">{monthToMonthFee}</EditableToken></span> },
    { name: 'Intra-Community Transfer Fee', value: intraCommunityTransferFee, frequency: 'Per Occurrence', feeType: 'Situational', badge: 'situational', node: <span className={costClass(intraCommunityTransferFee)}><EditableToken path="feetransparency.intra_community_transfer_fee" as="span">{intraCommunityTransferFee}</EditableToken></span> },
    { name: 'Early Termination / Insufficient Notice Fee', value: earlyTerminationFee, frequency: 'Per Occurrence', feeType: 'Situational', badge: 'situational', node: <span className={costClass(earlyTerminationFee)}><EditableToken path="feetransparency.early_termination_fee" as="span">{earlyTerminationFee}</EditableToken></span> },
    { name: 'Non-Refundable Risk Fee (Move-in Condition Fee)', value: riskFee, frequency: 'Per Occurrence', feeType: 'Situational', badge: 'situational', node: <span className={costClass(riskFee)}><EditableToken path="feetransparency.risk_fee" as="span">{riskFee}</EditableToken></span> },
    { name: 'Move-out Cleaning Fee', value: moveOutCleaningFee, frequency: 'Per Occurrence', feeType: 'Situational', badge: 'situational', node: <span className={costClass(moveOutCleaningFee)}><EditableToken path="feetransparency.move_out_cleaning_fee" as="span">{moveOutCleaningFee}</EditableToken></span> },
    { name: 'Late Fee', value: lateFee, frequency: 'Per Occurrence', feeType: 'Situational', badge: 'situational', node: <span className={costClass(lateFee)}><EditableToken path="feetransparency.late_fee" as="span">{lateFee}</EditableToken></span> },
  ];

  const showRequired = showAll || requiredRows.some((r) => !isNA(r.value)) || !isNA(residentServicesTotal);
  const showOptional = showAll || optionalRows.some((r) => !isNA(r.value));
  const showSituational = showAll || situationalRows.some((r) => !isNA(r.value));
  const showResidentTotal = showAll || !isNA(residentServicesTotal);

  const residentTotalFooter = showResidentTotal ? (
    <div className="ft-total">
      <span className="ft-total-label">Resident Services Total Per Month</span>
      <span className="ft-total-value">
        <span className={costClass(residentServicesTotal)}><EditableToken path="feetransparency.resident_services_total" as="span">{residentServicesTotal}</EditableToken></span>
      </span>
    </div>
  ) : null;

  const anyVisible = showRequired || showOptional || showSituational;

  return (
    <section className="ft-root">
      <div className={cn('ft-inner', wide && 'ft-inner--wide')}>
        {showHeader && (
          <>
            <EditableToken
              path="feetransparency.heading"
              as="h2"
              className="ft-heading"
            >
              {heading}
            </EditableToken>
            <RichTextContent
              path="feetransparency.intro"
              as="div"
              defaultValue="<p>The fees below apply to this community. Amounts shown are the most current figures on file and are subject to change. &ldquo;&mdash;&rdquo; indicates a fee that does not apply at this property. Please contact the leasing office for full details.</p>"
              className="ft-blurb"
            />
          </>
        )}

        <div className="ft-accordion">
          {showRequired && (
            <FeePanel
              title="Figures to Help You Figure it Out"
              badgeLabel="Required"
              badgeVariant="required"
              description={
                <RichTextContent
                  path="feetransparency.required_intro"
                  as="div"
                  defaultValue="<p>Essential fees and utilities to help you understand your base costs and requirements.</p>"
                  className="ft-panel-desc"
                />
              }
              rows={requiredRows}
              open={isEditMode || !!openSections.required}
              onToggle={() => toggle('required')}
              showAll={showAll}
              footer={residentTotalFooter}
            />
          )}
          {showOptional && (
            <FeePanel
              title="Extras that Matter"
              badgeLabel="Optional"
              badgeVariant="optional"
              description={
                <RichTextContent
                  path="feetransparency.optional_intro"
                  as="div"
                  defaultValue="<p>Optional services, amenities, and rentable spaces to enhance your living experience. Typically charged per apartment but may be per person or pet. Contact the Leasing Team for further clarification.</p>"
                  className="ft-panel-desc"
                />
              }
              rows={optionalRows}
              open={isEditMode || !!openSections.optional}
              onToggle={() => toggle('optional')}
              showAll={showAll}
            />
          )}
          {showSituational && (
            <FeePanel
              title="Situational Fees"
              badgeLabel="Optional"
              badgeVariant="situational"
              description={
                <RichTextContent
                  path="feetransparency.situational_intro"
                  as="div"
                  defaultValue="<p>Sometimes life happens, and we understand things don&rsquo;t always go as planned. Situational fees may apply in certain circumstances and can include charges related to damage, excessive cleaning, or other incident-based services. These fees help us maintain a safe, clean, and comfortable community for all residents.</p>"
                  className="ft-panel-desc"
                />
              }
              rows={situationalRows}
              open={isEditMode || !!openSections.situational}
              onToggle={() => toggle('situational')}
              showAll={showAll}
            />
          )}
          {!anyVisible && (
            <p className="ft-none">
              No additional fees apply at this community. Please contact the
              leasing office for full details.
            </p>
          )}
        </div>

        <RichTextContent
          path="feetransparency.disclaimer"
          as="div"
          defaultValue="<p>All fees are subject to change without notice and may vary by floor plan, lease term, and qualification. This sheet is provided for transparency and does not constitute an offer to lease. Please contact the leasing office for the fees and deposits that apply to your specific home.</p>"
          className="ft-disclaimer"
        />
      </div>

      <style>{`
        .ft-root {
          background: color-mix(in srgb, var(--vibe-color-text) 4%, var(--vibe-color-background));
          padding: 3.5rem 0 4.5rem;
        }
        @media (min-width: 768px) {
          .ft-root { padding: 4.5rem 0 5.5rem; }
        }

        .ft-inner {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        .ft-inner--wide {
          max-width: 90rem;
        }
        @media (min-width: 640px) {
          .ft-inner { padding: 0 1.5rem; }
        }
        @media (min-width: 1024px) {
          .ft-inner { padding: 0 2rem; }
          .ft-inner--wide { padding: 0 2.5rem; }
        }
        @media (min-width: 1400px) {
          .ft-inner--wide {
            max-width: 100rem;
            padding: 0 3rem;
          }
        }

        .ft-heading {
          margin: 0 0 0.75rem;
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.5rem, 2.5vw, 2rem);
          font-weight: 400;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--vibe-color-text);
          line-height: 1.2;
        }

        .ft-blurb {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.6;
          color: var(--vibe-color-text-muted);
          margin-bottom: 1.5rem;
          max-width: 48rem;
        }
        .ft-blurb p { margin: 0 0 0.75em; }
        .ft-blurb p:last-child { margin-bottom: 0; }
        .ft-blurb a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        .ft-accordion {
          display: flex;
          flex-direction: column;
          gap: 0.625rem;
        }

        .ft-panel {
          background: #ffffff;
          border-radius: 12px;
          overflow: hidden;
        }

        .ft-panel-trigger {
          all: unset;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          width: 100%;
          padding: 1.5rem;
          cursor: pointer;
          background: #ffffff;
          text-align: left;
          transition: background-color 200ms ease;
        }
        .ft-panel-trigger:hover {
          background: color-mix(in srgb, var(--vibe-color-text) 2%, #ffffff);
        }
        .ft-panel-trigger:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: -2px;
        }

        .ft-panel-title {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.75rem 0.95rem;
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.15rem, 2vw, 1.5rem);
          font-weight: 400;
          line-height: 1.3;
          color: var(--vibe-color-text);
        }

        .ft-badge {
          display: inline-block;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          font-weight: 700;
          line-height: 1.2;
          letter-spacing: 0.03em;
          padding: 0.25rem 0.5rem;
          border-radius: 7px;
          vertical-align: middle;
          white-space: nowrap;
        }
        .ft-badge--required {
          background: #000000;
          color: #ffffff;
        }
        .ft-badge--optional,
        .ft-badge--situational {
          background: #e2e2e2;
          color: #000000;
        }

        .ft-panel-icon {
          position: relative;
          flex: 0 0 auto;
          width: 1.75rem;
          height: 1.75rem;
          border-radius: 9999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .ft-panel-icon::after {
          content: '+';
          font-family: var(--vibe-font-body);
          font-size: 1.75rem;
          font-weight: 300;
          line-height: 1;
          color: #4d5259;
          position: relative;
          top: -0.1em;
          transition: transform 250ms ease;
        }
        .ft-panel--open .ft-panel-icon::after {
          content: '−';
          top: 0;
        }

        .ft-panel-body {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 350ms ease;
          background: #ffffff;
        }
        .ft-panel-body[data-open='true'] {
          grid-template-rows: 1fr;
        }
        .ft-panel-body-inner {
          overflow: hidden;
          padding: 0 1.5rem;
        }
        .ft-panel-body[data-open='true'] .ft-panel-body-inner {
          padding-bottom: 1.5rem;
        }

        .ft-panel-desc {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.45;
          font-weight: 400;
          color: #686d74;
          margin-bottom: 1.5rem;
        }
        .ft-panel-desc p { margin: 0 0 0.5em; }
        .ft-panel-desc p:last-child { margin-bottom: 0; }

        .ft-table-wrap {
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        .ft-table {
          width: 100%;
          min-width: 36rem;
          border-collapse: collapse;
          table-layout: fixed;
          margin: 0;
        }
        .ft-table th {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 500;
          line-height: 1.4;
          color: #000000;
          text-align: left;
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #e0e0e0;
        }
        /*
          Even four-column grid (Pointe at River City floor-plans pattern):
          Fee Name · Cost · Frequency · Fee Type. Fixed layout + % widths keep
          values locked under their headings even when names/costs are long.
        */
        .ft-table th:nth-child(1),
        .ft-table td:nth-child(1) {
          width: 40%;
          padding-left: 0;
        }
        .ft-table th:nth-child(2),
        .ft-table td:nth-child(2) {
          width: 22%;
          text-align: left;
        }
        .ft-table th:nth-child(3),
        .ft-table td:nth-child(3) {
          width: 22%;
          text-align: left;
          white-space: nowrap;
        }
        .ft-table th:nth-child(4),
        .ft-table td:nth-child(4) {
          width: 16%;
          text-align: right;
          padding-right: 0;
          white-space: nowrap;
        }
        .ft-table td {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.4;
          font-weight: 400;
          color: #4d5259;
          padding: 0.95rem 1rem;
          border-bottom: 1px solid #e0e0e0;
          vertical-align: middle;
          overflow-wrap: anywhere;
        }
        .ft-table tr:last-child td { border-bottom: none; }
        .ft-cost {
          border-bottom: 2px dotted currentColor;
          font-variant-numeric: tabular-nums;
        }
        .ft-cost--dollar::before {
          content: '$';
        }
        .ft-total-value .ft-cost {
          border-bottom: none;
        }

        .ft-total {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 1.5rem;
          margin-top: 0.75rem;
          padding: 1rem 0;
          border-top: 2px solid var(--vibe-color-primary);
          background: color-mix(in srgb, var(--vibe-color-primary) 8%, #ffffff);
        }
        .ft-total-label {
          font-family: var(--vibe-font-heading);
          font-size: 0.85rem;
          font-weight: 500;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--vibe-color-text);
        }
        .ft-total-value {
          flex: 0 0 auto;
          font-family: var(--vibe-font-heading);
          font-size: 1.15rem;
          font-weight: 500;
          font-variant-numeric: tabular-nums;
          text-align: right;
          color: var(--vibe-color-primary);
        }

        .ft-empty {
          margin: 0;
          padding: 0.5rem 0 0.25rem;
          font-family: var(--vibe-font-body);
          font-size: 0.9375rem;
          font-style: italic;
          color: var(--vibe-color-text-muted);
        }

        .ft-none {
          margin: 0;
          padding: 1.5rem;
          border-radius: 12px;
          background: #ffffff;
          font-family: var(--vibe-font-body);
          font-size: 0.9375rem;
          font-style: italic;
          color: var(--vibe-color-text-muted);
        }

        .ft-disclaimer {
          margin-top: 2rem;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          line-height: 1.6;
          color: var(--vibe-color-text-muted);
        }
        .ft-disclaimer p { margin: 0 0 0.5em; }
        .ft-disclaimer p:last-child { margin-bottom: 0; }
        .ft-disclaimer a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        /* Tablet: keep the real table + row dividers; slightly tighter padding. */
        @media (max-width: 1200px) {
          .ft-panel-title {
            flex-direction: column;
            align-items: flex-start;
          }
          .ft-table {
            min-width: 0;
          }
          .ft-table th,
          .ft-table td {
            padding-left: 0.75rem;
            padding-right: 0.75rem;
          }
          .ft-table th:nth-child(1),
          .ft-table td:nth-child(1) { padding-left: 0; }
          .ft-table th:nth-child(4),
          .ft-table td:nth-child(4) { padding-right: 0; }
        }

        /*
          Mobile: stack each fee as a full-width label/value block so Cost,
          Frequency, and Fee Type are all visible without horizontal scroll.
          Row dividers stay between fees.
        */
        @media (max-width: 767px) {
          .ft-panel-trigger {
            padding: 1.125rem;
            align-items: flex-start;
          }
          .ft-panel-title { font-size: 1.25rem; width: 80%; }
          .ft-panel-body-inner { padding-left: 1.125rem; padding-right: 1.125rem; }
          .ft-panel-body[data-open='true'] .ft-panel-body-inner {
            padding-bottom: 1.125rem;
          }
          .ft-table-wrap {
            overflow-x: visible;
          }
          .ft-table {
            min-width: 0;
          }
          .ft-table thead { display: none; }
          .ft-table,
          .ft-table tbody,
          .ft-table tr,
          .ft-table td {
            display: block;
            width: 100%;
          }
          .ft-table tr {
            padding: 0.85rem 0;
            border-bottom: 1px solid #e0e0e0;
          }
          .ft-table tr:last-child {
            border-bottom: none;
          }
          .ft-table td {
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            gap: 1rem;
            width: auto;
            padding: 0.3rem 0;
            border-bottom: none;
            text-align: right;
            white-space: normal;
            font-size: 0.9375rem;
          }
          .ft-table td::before {
            content: attr(data-label);
            flex: 0 1 auto;
            font-weight: 600;
            color: #2c3e50;
            text-align: left;
          }
          .ft-table td:first-child {
            justify-content: flex-start;
            text-align: left;
            font-weight: 500;
            color: var(--vibe-color-text);
            padding-bottom: 0.45rem;
          }
          .ft-table td:first-child::before {
            display: none;
          }
          .ft-table th:nth-child(2),
          .ft-table td:nth-child(2),
          .ft-table th:nth-child(3),
          .ft-table td:nth-child(3),
          .ft-table th:nth-child(4),
          .ft-table td:nth-child(4) {
            width: auto;
            text-align: right;
            white-space: normal;
          }
          .ft-disclaimer { text-align: center; }
        }

        @media (prefers-reduced-motion: reduce) {
          .ft-panel-body,
          .ft-panel-icon::after {
            transition: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FeeTransparency',
  displayName: 'Fee Transparency Table',
  tokens: {
    'feetransparency.heading': {
      label: 'Heading',
      type: 'text',
      defaultValue: 'Fee Transparency',
    },
    'feetransparency.intro': {
      label: 'Intro Text',
      type: 'richtext',
      defaultValue: '<p>The fees below apply to this community. Amounts shown are the most current figures on file and are subject to change. &ldquo;&mdash;&rdquo; indicates a fee that does not apply at this property. Please contact the leasing office for full details.</p>',
    },
    'feetransparency.required_intro': {
      label: 'Required Fees Intro',
      type: 'richtext',
      defaultValue: '<p>Essential fees and utilities to help you understand your base costs and requirements.</p>',
    },
    'feetransparency.optional_intro': {
      label: 'Optional Fees Intro',
      type: 'richtext',
      defaultValue: '<p>Optional services, amenities, and rentable spaces to enhance your living experience. Typically charged per apartment but may be per person or pet. Contact the Leasing Team for further clarification.</p>',
    },
    'feetransparency.situational_intro': {
      label: 'Situational Fees Intro',
      type: 'richtext',
      defaultValue: '<p>Sometimes life happens, and we understand things don&rsquo;t always go as planned. Situational fees may apply in certain circumstances and can include charges related to damage, excessive cleaning, or other incident-based services. These fees help us maintain a safe, clean, and comfortable community for all residents.</p>',
    },

    'feetransparency.application_fee': { label: 'Application Fee', type: 'text', defaultField: 'property_application_fee', defaultValue: '—' },
    'feetransparency.administrative_fee': { label: 'Administrative Fee', type: 'text', defaultField: 'property_admin_fee', defaultValue: '—' },
    'feetransparency.security_deposit': { label: 'Security Deposit', type: 'text', defaultValue: '—' },
    'feetransparency.reservation_fee': { label: 'Reservation Fee', type: 'text', defaultValue: '—' },
    'feetransparency.month_to_month_fee': { label: 'Month-to-Month Fee', type: 'text', defaultValue: '—' },
    'feetransparency.risk_fee': { label: 'Non-Refundable Risk Fee (Move-in Condition Fee)', type: 'text', defaultValue: '—' },
    'feetransparency.move_out_cleaning_fee': { label: 'Move-out Cleaning Fee', type: 'text', defaultValue: '—' },
    'feetransparency.electricity_connection_fee': { label: 'Electricity Connection Fee', type: 'text', defaultValue: '—' },
    'feetransparency.electricity_disconnection_fee': { label: 'Electricity Disconnection Fee', type: 'text', defaultValue: '—' },
    'feetransparency.utility_electric': { label: 'Utility — Electric', type: 'text', defaultValue: '—' },
    'feetransparency.utility_gas': { label: 'Utility — Gas', type: 'text', defaultValue: '—' },
    'feetransparency.utility_water_sewer': { label: 'Utility — Water/Sewer', type: 'text', defaultValue: '—' },
    'feetransparency.utility_reimb_fee': { label: 'Utility Reimbursement Fee', type: 'text', defaultValue: '—' },
    'feetransparency.utility_setup_fee': { label: 'Utility Set-up Fee', type: 'text', defaultValue: '—' },
    'feetransparency.utility_disconnect_fee': { label: 'Utility Disconnect Fee', type: 'text', defaultValue: '—' },
    'feetransparency.utility_admin_billing_fee': { label: 'Utility Administrative Billing Fee', type: 'text', defaultValue: '—' },
    'feetransparency.parking_carport': { label: 'Parking Fee — Carport', type: 'text', defaultValue: '—' },
    'feetransparency.parking_garage': { label: 'Parking Fee — Garage', type: 'text', defaultValue: '—' },
    'feetransparency.parking_monthly': { label: 'Parking Fee — Monthly', type: 'text', defaultValue: '—' },
    'feetransparency.parking_pass': { label: 'Parking Pass', type: 'text', defaultValue: '—' },
    'feetransparency.storage_unit_fee': { label: 'Storage Unit Fee', type: 'text', defaultValue: '—' },
    'feetransparency.short_term_rental_fee': { label: 'Short-Term Rental Fee', type: 'text', defaultValue: '—' },
    'feetransparency.access_device_cost': { label: 'Access Device Cost', type: 'text', defaultValue: '—' },
    'feetransparency.early_termination_fee': { label: 'Early Termination / Insufficient Notice Fee', type: 'text', defaultValue: '—' },
    'feetransparency.intra_community_transfer_fee': { label: 'Intra-Community Transfer Fee', type: 'text', defaultValue: '—' },
    'feetransparency.late_fee': { label: 'Late Fee', type: 'text', defaultValue: '—' },
    'feetransparency.credit_builder': { label: 'Credit Builder', type: 'text', defaultValue: '—' },
    'feetransparency.occupancy_fee': { label: 'Occupancy Fee', type: 'text', defaultValue: '—' },
    'feetransparency.pet_rent': { label: 'Pet Rent', type: 'text', defaultField: 'property_monthly_pet_rent', defaultValue: '—' },
    'feetransparency.pet_fee': { label: 'Pet Fee', type: 'text', defaultField: 'property_upfront_pet_fee', defaultValue: '—' },
    'feetransparency.pet_fee_second_pet': { label: 'Pet Fee (Second Pet)', type: 'text', defaultValue: '—' },
    'feetransparency.pet_rent_second_pet': { label: 'Pet Rent (Second Pet)', type: 'text', defaultValue: '—' },

    'feetransparency.community_amenity_fee': { label: 'Community Amenity Fee', type: 'text', defaultValue: '—' },
    'feetransparency.valet_trash_fee': { label: 'Valet Trash Fee', type: 'text', defaultValue: '—' },
    'feetransparency.pest_control_fee': { label: 'Pest Control Fee', type: 'text', defaultValue: '—' },
    'feetransparency.resident_tech_fee': { label: 'Resident Tech Fee', type: 'text', defaultValue: '—' },
    'feetransparency.trash_non_valet': { label: 'Trash Fee — Non-Valet', type: 'text', defaultValue: '—' },
    'feetransparency.trash_reimb_fee': { label: 'Trash Reimbursement Fee', type: 'text', defaultValue: '—' },
    'feetransparency.trash_admin_fee': { label: 'Trash Administrative Fee', type: 'text', defaultValue: '—' },
    'feetransparency.trash_hauling_fee': { label: 'Trash Hauling Fee', type: 'text', defaultValue: '—' },
    'feetransparency.building_liability_captive': { label: 'Building Liability Captive', type: 'text', defaultValue: '—' },
    'feetransparency.pool_fee': { label: 'Pool Fee', type: 'text', defaultValue: '—' },
    'feetransparency.package_lockers': { label: 'Package Lockers', type: 'text', defaultValue: '—' },
    'feetransparency.washer_dryer_fee': { label: 'Washer/Dryer Fee', type: 'text', defaultValue: '—' },
    'feetransparency.resident_services_total': { label: 'Resident Services Total (Per Month)', type: 'text', defaultValue: '—' },

    'feetransparency.disclaimer': {
      label: 'Disclaimer',
      type: 'richtext',
      defaultValue: '<p>All fees are subject to change without notice and may vary by floor plan, lease term, and qualification. This sheet is provided for transparency and does not constitute an offer to lease. Please contact the leasing office for the fees and deposits that apply to your specific home.</p>',
    },
  },
};

(FeeTransparency as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FeeTransparency;
